import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import toast from 'react-hot-toast'
import {
  CheckIcon,
  SparklesIcon,
  ArrowRightIcon,
  ShoppingBagIcon,
  TruckIcon,
  BuildingOffice2Icon,
  HeartIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UserIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline'
import { PRODUCTS, UNITS_PER_BOX, PRICE_PER_BOX } from '../data/products'
import { motion } from 'framer-motion'

const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.div>
)

const testimonials = [
  {
    quote:
      "We introduced Twiggle bamboo toothbrushes into our patient hygiene kits, and the response has been extremely positive. Patients have shared that they love the soft bristles, comfortable feel, and eco-friendly packaging. It's rewarding to offer a product that not only supports oral health but also makes patients feel good about making a sustainable choice.",
    name: 'Dr. Mukarram Muneer DDS (NYU)',
    avatar: '/images/project/dr.jpeg',
  },
  {
    quote:
      'Twiggle has been a wonderful partner for our clinic. The personalized bamboo brushes help us stand out, and patients consistently ask where they can get more. Quality and sustainability in one package.',
    name: 'Dr. Sarah Chen, DDS',
    avatar: '/images/project/ceo.png',
  },
  {
    quote:
      'We use Twiggle brushes for community outreach and school visits. Parents appreciate the eco-friendly message, and kids love the kid-sized options. Highly recommend for any practice focused on preventive care.',
    name: 'Dr. James Patel, DMD',
    avatar: '/images/project/dental.png',
  },
]

const partnerOrgs = [
  'Brushamania',
  'Scarborough Rotary Passport Club',
  'DURHAM ONTARIO DENTAL SOCIETY',
  'Uxbridge family dentistry',
]

const Home = () => {
  const navigate = useNavigate()

  const adultProducts = PRODUCTS.filter((p) => p.category === 'adult')
  const kidsProducts = PRODUCTS.filter((p) => p.category === 'kids')

  const statsBar = [
    { value: '144', label: 'Units per box' },
    { value: '3–4', label: 'Week delivery' },
    { value: '0', label: 'Plastic in handles' },
    { value: '100%', label: 'Compostable handle' },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section
        className="relative flex min-h-[88vh] sm:min-h-[92vh] flex-col justify-center overflow-hidden"
        aria-label="Hero section"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="/images/project/all-products-hero.jpeg"
            alt="Twiggle Oral Care - All sustainable bamboo products"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/10 lg:from-white/95 lg:via-white/80 lg:to-transparent" />
        </div>

        <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4 sm:mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] sm:text-xs font-bold tracking-wide text-emerald-800 uppercase shadow-sm"
            >
              Ontario’s First Sustainable Dental Brand <span className="text-emerald-300">|</span> Locally Owned
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-slate-900 leading-tight tracking-tight"
            >
              Personalized <br className="hidden sm:block" />
              Professional-grade <br className="hidden sm:block" />
              Toothbrushes <br className="hidden sm:block" />
              <span className="text-emerald-600 font-black">for Dental Practices</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-4 sm:mt-8 max-w-xl text-base sm:text-xl font-medium leading-relaxed text-slate-600"
            >
              Premium bamboo toothbrushes designed for dental practices
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-5"
            >
              <button
                id="hero-shop-btn"
                onClick={() => navigate('/shop')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-bold text-white shadow-lg shadow-emerald-600/10 transition-all duration-300 hover:bg-emerald-700 hover:scale-[1.02]"
              >
                <ShoppingBagIcon className="w-5 h-5" />
                Order for Your Clinic
              </button>
              <button
                id="hero-bulk-btn"
                onClick={() => navigate('/bulk-orders')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-bold text-slate-700 shadow-sm transition-all duration-300 hover:bg-slate-50 hover:border-slate-300"
              >
                Request a Bulk Quote
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 sm:mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100/80 text-emerald-600">
                  <GlobeAltIcon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Sustainable</div>
                  <div className="text-[11px] text-slate-500 font-medium leading-none">Eco-friendly by design</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100/80 text-emerald-600">
                  <ShieldCheckIcon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Professional-Grade</div>
                  <div className="text-[11px] text-slate-500 font-medium leading-none">Built for daily use</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100/80 text-emerald-600">
                  <HeartIcon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Thoughtful</div>
                  <div className="text-[11px] text-slate-500 font-medium leading-none">Better care for every smile</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative z-20 border-t border-slate-100 bg-white/70 backdrop-blur-md py-5 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {statsBar.map((s, i) => (
                <li key={i} className="text-center sm:text-left px-2 py-2 sm:py-0">
                  <div className="text-xl sm:text-2xl font-black text-slate-800 leading-tight">{s.value}</div>
                  <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700 font-extrabold leading-tight">{s.label}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-16 sm:py-20 bg-gray-50" aria-label="Product categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-10 sm:mb-14">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
              <SparklesIcon className="w-4 h-4" />
              Sold in boxes of {UNITS_PER_BOX} units
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900">Our Product Range</h2>
            <p className="mt-3 text-gray-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Bamboo toothbrushes for adults and children — eco-friendly, BPA-free, and supplied in boxes of {UNITS_PER_BOX}, ready for dental clinics and patients.
            </p>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
              <span className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center text-sm">
                <UserIcon className="w-4 h-4 text-primary-600" />
              </span>
              Adult Bamboo Toothbrushes
            </h3>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {adultProducts.map((product, i) => (
              <FadeIn key={product.id} delay={i * 0.1}>
                <ProductCard product={product} />
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.1}>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
              <span className="w-7 h-7 bg-pink-100 rounded-full flex items-center justify-center text-sm">
                <FaceSmileIcon className="w-4 h-4 text-pink-600" />
              </span>
              Kids Bamboo Toothbrushes
            </h3>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
            {kidsProducts.map((product, i) => (
              <FadeIn key={product.id} delay={i * 0.1}>
                <ProductCard product={product} />
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2} className="mt-10 text-center">
            <Link
              to="/shop"
              id="home-view-all-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold rounded-xl transition-all duration-300"
            >
              View All Products
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 sm:py-20 bg-white" aria-label="Testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900">
              Trusted by <span className="text-primary-600">Dental Professionals</span>
            </h2>
            <p className="mt-3 text-gray-500 text-base sm:text-lg max-w-xl mx-auto">
              Hear from clinics using Twiggle bamboo toothbrushes in their patient care programs.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((t, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <blockquote className="flex h-full flex-col rounded-2xl border border-gray-100 bg-gray-50 p-6 sm:p-8">
                  <p className="flex-1 text-sm sm:text-base text-gray-700 leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
                  <footer className="mt-6 flex items-center gap-4 border-t border-gray-200 pt-5">
                    <img
                      src={t.avatar}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover bg-primary-100 border border-gray-200"
                    />
                    <cite className="text-sm font-bold text-gray-900 not-italic">{t.name}</cite>
                  </footer>
                </blockquote>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PROMOTE YOUR PRACTICE */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-gray-50 to-white" aria-label="Promote your practice">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeIn>
              <div className="relative">
                <img
                  src="/images/project/custom-engraved-brushes.jpeg"
                  alt="Twiggle bamboo toothbrush clinic mockup"
                  className="rounded-2xl shadow-xl w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute -bottom-4 -left-4 bg-primary-600 text-white px-4 sm:px-5 py-2 sm:py-3 rounded-xl shadow-lg text-xs sm:text-sm font-semibold">
                  100% Sustainable Packaging
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
                            <div className="space-y-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                  <BuildingOffice2Icon className="w-4 h-4" />
                  For Dental Clinics & Organizations
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                  Promote Your Practice
                </h2>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                  Promoting your practice with personalized oralcare products, like bulk personalized toothbrushes, help to increase awareness, build your reputation, and grow referrals.
                </p>
                <ul className="space-y-3">
                  {[
                    'Custom logo engraving on every brush',
                    'Premium bamboo handles with smooth, comfortable grip',
                    'Soft, durable bristles for gentle effective cleaning',
                    'Eco-friendly and biodegradable, supporting a greener future',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-3.5 h-3.5 text-primary-600" />
                      </span>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/shop')}
                  id="why-buy-btn"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all duration-300 mt-2"
                >
                  Shop Now
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CUSTOM PACKAGING */}
      <section className="py-16 sm:py-20 bg-primary-50" aria-label="Custom brush inquiry">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <FadeIn>
              <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-6">Custom Brush & Custom Box</h2>
              <p className="text-gray-600 text-base sm:text-lg max-w-xl mb-6 leading-relaxed">
                Want a fully customized aesthetic for your clinic? We offer custom-designed brushes and specialized box packaging. Let us know what you need, and we&apos;ll bring your vision to life.
              </p>
              <button
                onClick={() => navigate('/bulk-orders')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all"
              >
                Learn About Custom Packaging
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </FadeIn>
            <FadeIn delay={0.1}>
              <img
                src="/images/project/smile-dental-green.jpeg"
                alt="Smile Dental Care clinic with custom Twiggle packaging"
                className="rounded-2xl shadow-lg w-full object-cover"
                loading="lazy"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* GIVING BACK */}
      <section className="py-16 sm:py-20 bg-white" aria-label="Giving back to the community">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <HeartIcon className="w-10 h-10 text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-6">Giving Back to the Community</h2>
            <div className="space-y-4 text-gray-600 text-base sm:text-lg leading-relaxed">
              <p>At Twiggle Oral Care, we believe every child deserves access to healthy oral care habits.</p>
              <p>That&apos;s why a portion of every purchase helps us donate sustainable bamboo toothbrushes and oral hygiene essentials to underprivileged children&apos;s and community programs through dent-o-kind organization.</p>
              <p>Together, we&apos;re creating healthier smiles while reducing plastic waste — one toothbrush at a time.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 sm:py-20 bg-gray-50" aria-label="Chosen by dental and community organizations">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">Chosen by Dental & Community Organizations</h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-8">
              Twiggle Oral Care bamboo toothbrushes are being used by clinics, community organizations, and wellness initiatives committed to sustainability.
            </p>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Including:</p>
            <ul className="flex flex-wrap justify-center gap-3">
              {partnerOrgs.map((org) => (
                <li key={org} className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm">
                  {org}
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* CUSTOM PACKAGING CTA */}
      <section className="py-16 sm:py-20 bg-primary-700" aria-label="Custom packaging call to action">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-5 text-white">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
                  <TruckIcon className="w-4 h-4" />
                  Minimum {UNITS_PER_BOX} toothbrushes per box
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Custom Packaging for Dental Clinics</h2>
                <p className="text-primary-100 text-base sm:text-lg leading-relaxed">
                  Twiggle Oral Care supplies bamboo toothbrushes for dental clinics, oral health programs, community initiatives, and events across Canada.
                </p>
                <button
                  id="home-bulk-quote-btn"
                  onClick={() => navigate('/bulk-orders')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 font-bold rounded-xl hover:bg-primary-50 transition-all duration-300"
                >
                  Explore Custom Packaging
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} className="hidden lg:block">
              <img
                src="/images/project/all-products-hero.jpeg"
                alt="Custom packaging for dental clinics"
                className="w-full rounded-2xl shadow-2xl object-cover h-80"
                loading="lazy"
              />
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  )
}

const ProductCard = ({ product }) => {
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const [added, setAdded] = useState(false)

  const handleAdd = (e) => {
    e.stopPropagation()
    addToCart(product, 1, null, null)
    setAdded(true)
    toast.success(`${product.fullName} added to cart!`)
    setTimeout(() => setAdded(false), 2000)
  }

  return (
    <div 
      onClick={() => navigate(`/shop/${product.slug}`)}
      className="cursor-pointer bg-white rounded-2xl shadow-sm hover:shadow-md border border-gray-100 overflow-hidden flex flex-col h-full group transition-all duration-300"
    >
      <div className="relative bg-gray-50 flex items-center justify-center p-6 h-48 sm:h-52">
        <img
          src={product.image}
          alt={product.fullName}
          className="max-h-36 sm:max-h-40 w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-3 right-3 text-xs bg-primary-100 text-primary-700 font-semibold px-2.5 py-1 rounded-full">
          {product.badge}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex-1">
          <p className="text-xs font-medium text-primary-600 uppercase tracking-wider mb-1">
            {product.category === 'kids' ? 'Kids' : 'Adult'}
          </p>
          <h3 className="text-base font-bold text-gray-900">{product.fullName}</h3>
          <p className="text-gray-500 text-sm mt-1.5 leading-relaxed line-clamp-2">{product.shortDescription}</p>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-extrabold text-gray-900">${product.pricePerBox.toFixed(2)}</p>
            <p className="text-[10px] text-gray-500">per box · {UNITS_PER_BOX} brushes</p>
          </div>
          <button
            onClick={handleAdd}
            className={`flex-shrink-0 px-4 py-2 text-sm font-semibold border-2 rounded-lg transition-all duration-200 ${
              added 
                ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700' 
                : 'text-primary-600 border-primary-600 hover:bg-primary-600 hover:text-white'
            }`}
          >
            {added ? 'Added!' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Home


